import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import FieldGenericSelect from './FieldGenericSelect';
import FieldInput from './FieldInput';
import Tooltip from '../../misc/Tooltip';

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right: 0;
  top: 0;
`;

const StyledFieldHeader = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 6px;
  padding-bottom: 4px;
  padding-right: 20px;
`;

const StyledDropdownWrapDisplay = styled.div`
  width: 100%;
  background-color: ${(props) => props.backgroundColor};
  font-size: ${(props) => props.fontSize}px !important;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  border-radius: ${(props) => props.borderRadius}px;
  border: ${(props) => props.border};
  padding: ${(props) => props.padding}px;
  min-height: 25px;
  box-sizing: border-box;
  white-space: pre-line;
  overflow-wrap: break-word;
  cursor: pointer;

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

class DropdownGenericField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,
    inputGroupWrapMode: PropTypes.bool,

    type: PropTypes.string,
    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    value: PropTypes.string,
    disabled: PropTypes.bool,
    options: PropTypes.array,
    tabIndex: PropTypes.number,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

  }

  constructor(props) {
    super(props);
    this.state = { focused: false };
    this.wrapDisplayHeight = null;
    this.wrapModeSelectRef = null;
  }

  componentDidUpdate(prevProps, prevState) {
    const isWrapMode = this.props.inputGroupWrapMode && this.props.textOverflow !== 'ellipsis';
    if (
      isWrapMode
      && !this.props.disabled
      && this.state.focused
      && !prevState.focused
      && this.wrapModeSelectRef
    ) {
      const el = this.wrapModeSelectRef;
      window.requestAnimationFrame(() => {
        if (!this.state.focused || this.wrapModeSelectRef !== el) return;
        try {
          if (typeof el.showPicker === 'function') {
            el.showPicker();
          } else {
            el.click();
          }
        } catch (err) {
          try {
            el.click();
          } catch (e2) {
            // Native list may require a second user interaction in some browsers
          }
        }
      });
    }
  }

  getSelectedOptionText = () => {
    const { options, value } = this.props;
    if (!options || options.length < 1 || !value) return '';
    const selectedOption = options.find((option) => option._id === value && !option.deleted);
    return selectedOption ? selectedOption.value : '';
  }

  render() {

    const {
      type,

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      options,
      tabIndex,

      //SDK Styles
      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      onDecoratorClick,
      fieldMode,
      pageLayoutMode,
      fieldPresentationMode,
      decorators,
      inputGroupWrapMode,

    } = this.props;

    const { focused } = this.state;
    const border = `${borderWidth}px solid ${borderColor}`;
    const isWrapMode = inputGroupWrapMode && textOverflow !== 'ellipsis';

    let displayFieldNode;

    if (isWrapMode) {

      const wrapTypography = {
        fontSize,
        fontColor,
        fontStyle,
        fontWeight,
        textAlign,
        textTransform,
        textDecoration,
        borderRadius,
        borderWidth,
        backgroundColor,
        padding,
        primaryColor,
        border,
      };

      if (focused && !disabled) {
        displayFieldNode = (
          <FieldGenericSelect
            data-testid={`${fieldPositionId}-dropdown-generic-field`}
            autoFocus
            tabIndex={tabIndex}
            selectRef={(node) => { this.wrapModeSelectRef = node; }}
            {...{
              type,
              disabled,
              options,
              value,
              primaryColor,
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow,
              borderColor,
              borderRadius,
              borderWidth,
              backgroundColor,
              padding,
              fieldId,
              tipVisible,
              tipTitle,
              tipDescription,
            }}
            style={this.wrapDisplayHeight ? { height: `${this.wrapDisplayHeight}px`, minHeight: `${this.wrapDisplayHeight}px` } : undefined}
            onChange={(e) => {
              const nextValue = e.target.value;
              if (!disabled && nextValue !== value) {
                onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);
              }
            }}
            onFocus={(e) => {
              if (!disabled && onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
            }}
            onBlur={() => {
              this.setState({ focused: false });
              if (!disabled && onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
            }}
          />
        );
      } else {
        displayFieldNode = (
          <StyledDropdownWrapDisplay
            data-testid={`${fieldPositionId}-dropdown-generic-field`}
            data-ig-wrap-display="true"
            ref={(e) => { if (e) this.wrapDisplayHeight = e.offsetHeight; }}
            disabled={disabled}
            {...wrapTypography}
            onClick={() => {
              if (!disabled) {
                this.setState({ focused: true });
              }
            }}
          >
            {this.getSelectedOptionText()}
          </StyledDropdownWrapDisplay>
        );
      }

    } else {

      displayFieldNode = (
        <FieldGenericSelect
          data-testid={`${fieldPositionId}-dropdown-generic-field`}
          tabIndex={tabIndex}
          {...{
            type,
            disabled,
            options,
            value,
            primaryColor,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            fieldId,
            tipVisible,
            tipTitle,
            tipDescription,
          }}
          onChange={(e) => {
            const nextValue = e.target.value;
            if (!disabled && nextValue !== value) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);
            }
          }}
          onFocus={(e) => {
            if (!disabled && onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
          }}
          onBlur={() => {
            if (!disabled && onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
          }}
        />
      );

    }

    return (
      <StyledWrapper {...{ margin }}>
        <StyledFieldHeader>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          <FieldDecorators
            decorators={decorators}
            fieldMode={fieldMode}
            fieldId={fieldId}
            fieldIdentifier={fieldIdentifier}
            fieldPositionId={fieldPositionId}
            onDecoratorClick={onDecoratorClick}
          />
        </StyledFieldHeader>
        {displayFieldNode}
        {this.getTooltip({ ...this.props })}
      </StyledWrapper>
    );
  }

   getTooltip = (props) => {

     const { disabled, fieldId, tipTitle, tipDescription, tipVisible } = props;
     if (disabled) return null;

     return (
       <StyledTooltipWrapper>
         <Tooltip
           fieldId={fieldId}
           title={tipTitle}
           description={tipDescription}
           tipVisible={tipVisible}
         />
       </StyledTooltipWrapper>
     );

   };
}

export default DropdownGenericField;